import { occupiedDefaults } from './defaults';
import {
  OCCUPIED_APPROACHES,
  OCCUPIED_GATES,
  OCCUPIED_STANCES,
  type CrewDef,
  type OccupiedRulesDef,
  type OccupiedTellDef,
  type StanceGrid,
} from './types';

/*
 * The occupied preview (occupied.html §4 Admin "Preview: pick a crew and disposition, see the tells and stance odds"), from the
 * content: the crew's disposition odds, per gate the true tells that can show (1, or 2 at `trueTells.twoChance`) and the red herrings
 * (one at `herringChance`, from another disposition's tells at that gate), and per approach the stance odds from the grid.
 */

/** key → weight as shares of the total (positive weights only), in the given key order then any extra keys. */
export function shares(
  weights: Record<string, number> | null | undefined,
  order: readonly string[] = [],
) {
  const w = weights ?? {};
  const keys = [
    ...order.filter((k) => k in w),
    ...Object.keys(w).filter((k) => !order.includes(k)),
  ];
  const total = keys.reduce((s, k) => s + Math.max(0, w[k] ?? 0), 0);
  return keys
    .filter((k) => (w[k] ?? 0) > 0)
    .map((k) => ({ key: k, weight: w[k] ?? 0, p: total > 0 ? (w[k] ?? 0) / total : 0 }));
}

export type GatePreview = {
  gate: string;
  /** Tells pointing at the disposition: the pool true tells are drawn from. */
  truePool: OccupiedTellDef[];
  /** Tells at this gate pointing elsewhere: the herring pool. */
  herringPool: OccupiedTellDef[];
  /** P(2 true tells) — only when the pool has 2 or more. */
  pTwo: number;
  /** P(a herring shows) — only when the herring pool has one. */
  pHerring: number;
};

export type StanceOdds = { approach: string; stances: { key: string; p: number }[]; total: number };

export type OccupiedPreview = {
  dispositions: { key: string; weight: number; p: number }[];
  gates: GatePreview[];
  /** Far tells on the map card (yard tells with `map`). */
  mapTells: OccupiedTellDef[];
  stances: StanceOdds[];
  zombieMult: number;
};

export function previewOccupied(
  rules: OccupiedRulesDef | null | undefined,
  crew: CrewDef | null | undefined,
  tells: (OccupiedTellDef | null)[] | null | undefined,
  disposition: string,
): OccupiedPreview {
  const d = occupiedDefaults();
  const r = { ...d, ...(rules ?? {}) };
  const roll = { ...d.trueTells, ...(r.trueTells ?? {}) };
  const all = (tells ?? []).filter((t): t is OccupiedTellDef => t != null);
  const gates = OCCUPIED_GATES.map((gate) => {
    const at = all.filter((t) => t.gate === gate);
    const truePool = at.filter((t) => t.disposition === disposition);
    const herringPool = at.filter((t) => t.disposition !== disposition);
    return {
      gate,
      truePool,
      herringPool,
      pTwo: truePool.length >= 2 ? (roll.twoChance ?? 0) : 0,
      pHerring: herringPool.length ? (roll.herringChance ?? 0) : 0,
    };
  });
  const grid = (r.stances ?? {}) as StanceGrid;
  const stances = OCCUPIED_APPROACHES.map((approach) => {
    const w = grid[disposition]?.[approach] ?? {};
    const s = shares(w, OCCUPIED_STANCES);
    return {
      approach,
      stances: s.map(({ key, p }) => ({ key, p })),
      total: Object.values(w).reduce((a, x) => a + Math.max(0, x ?? 0), 0),
    };
  });
  const mult = r.zombieMult ?? {};
  return {
    dispositions: shares(crew?.dispositions),
    gates,
    mapTells: all.filter((t) => t.gate === 'yard' && t.map && t.disposition === disposition),
    stances,
    zombieMult: mult[disposition] ?? mult['default'] ?? 1,
  };
}

/** Sets one cell's weight list in the grid (a new object; empty cells and rows are dropped). */
export function setStanceCell(
  grid: StanceGrid | null | undefined,
  disposition: string,
  approach: string,
  weights: Record<string, number>,
): StanceGrid {
  const next: StanceGrid = structuredClone(grid ?? {});
  const row = { ...(next[disposition] ?? {}) };
  const clean = Object.fromEntries(Object.entries(weights).filter(([, w]) => w > 0));
  if (Object.keys(clean).length) row[approach] = clean;
  else delete row[approach];
  if (Object.keys(row).length) next[disposition] = row;
  else delete next[disposition];
  return next;
}
